import { gql, useMutation, useQuery, useApolloClient } from "@apollo/client";
import React, { useState, useContext, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useUserList } from "../../contexts/UserListContext";
import {
  LoadingView,
  StateStack,
  InvalidReasons,
  NotificationContext,
  Tabs,
} from "@solsys/solsys-reactjs";
import CraneEvent from "./CraneEvent";
import SingleView from "../mapview/SingleView";
import UserDropDown from "../../dropdowns/UserDropDown";
import TagDropDown from "../../dropdowns/TagDropDown";
import RegionDropDown from "../../dropdowns/RegionDropDown";
import CustomerDropDown from "../../dropdowns/CustomerDropDown";
import NotificationDropDown from "../../dropdowns/NotificationDropDown";
import TicketController from "../ticket/TicketController";
import ServiceController from "../service/ServiceController";
import InspectionIntervals from "./InspectionIntervals";
import SaveButton from "../elements/SaveButton";
import {
  useCreateIdempotencyKeys,
  fingerprintProperties,
  mutationInput,
} from "../../utils/idempotency";

const entityQuery = gql`
  query craneList($crane_list_request: crane_list_request
  $tag2Crane_list_request: tag2Crane_list_request
  $crane_subscription_list_request: crane_subscription_list_request
  ) {
    crane(crane_list_request: $crane_list_request) {
      edges {
        id
        name
        serial_no
        region_id
        customer_id
        longitude
        latitude
        primary_userid
        region {
          id
          name
        }
        customer {
          id
          name
        }
      }
      totalCount
    }
    tag2Crane(tag2Crane_list_request: $tag2Crane_list_request) {
      edges {
        id
        tag_id
      }
      totalCount
    }
    crane_subscription(crane_subscription_list_request: $crane_subscription_list_request) {
      edges {
        id
        user_id
      }
    }
  }
`;

const entityMutation = gql`
  mutation craneMutate($crane_mutation_request: MutationInput) {
    crane(crane_mutation_request: $crane_mutation_request) {
      id
      isNew
    }
  }
`;

const tag2CraneMutation = gql`
  mutation tag2CraneMutate($tag2Crane_mutation_request: MutationInput) {
    tag2Crane(tag2Crane_mutation_request: $tag2Crane_mutation_request) {
      id
      isNew
    }
  }
`;

const craneSubscriptionMutation = gql`
  mutation craneSubscriptionMutate($crane_subscription_mutation_request: MutationInput) {
    crane_subscription(crane_subscription_mutation_request: $crane_subscription_mutation_request) {
      id
      isNew
    }
  }
`;

const upperCaseFirstChar = (str) => {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}

export default function CraneEdit({
  id: entityId,
  action,
  navigate,
  tab,
  readOnly = false,
}) {
  const isEdit = action === "edit";
  const { addNotification } = useContext(NotificationContext);
  const client = useApolloClient();
  const [activeTab, setActiveTab] = useState(upperCaseFirstChar(tab) ?? 'Chat');
  const { user } = useAuth();
  const userList = useUserList();
  // Start new records straight in the form; existing records open as a read-only summary.
  const [isEditing, setIsEditing] = useState(!isEdit);
  // variables for entity query const
  let variables = {
    crane_list_request: {
      filters: [
        {
          field: "id",
          operator: "=",
          value: String(entityId),
        },
      ],
    },
    tag2Crane_list_request: {
      filters: [
        {
          field: "crane_id",
          operator: "=",
          value: String(entityId),
        },
      ],
    },
    crane_subscription_list_request: {
      filters: [
        {
          field: "crane_id",
          operator: "=",
          value: String(entityId),
        },
      ],
    },
  };

  const [name, setName] = useState(null);
  const [serialNo, setSerialNo] = useState(null);
  const [lon, setLon] = useState(null);
  const [lat, setLat] = useState(null);
  const [regionId, setRegionId] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [tagIds, setTagIds] = useState([]);
  const [tagList, setTagList] = useState([]);
  const [location, setLocation] = useState({});
  const [userIds, setUserIds] = useState([]);
  const [subscribedUserList, setSubscribedUserList] = useState([]);
  const [regionName, setRegionName] = useState(null);
  const [customerName, setCustomerName] = useState(null);

  const [invalidReasons, setInvalidReasons] = useState([]);
  const createKeys = useCreateIdempotencyKeys();
  const craneProperties = [
    { key: "name", string: name },
    { key: "serial_no", string: serialNo },
    ...(regionId ? [{ key: "region_id", number: Number(regionId) }] : [{ key: "region_id", number: null }]),
    ...(customerId ? [{ key: "customer_id", number: Number(customerId) }] : [{ key: "customer_id", number: null }]),
    ...(lon !== null && lon !== "" ? [{ key: "longitude", float: parseFloat(lon) }] : []),
    ...(lat !== null && lat !== "" ? [{ key: "latitude", float: parseFloat(lat) }] : []),
    ...(userId ? [{ key: "primary_userid", string: String(userId) }] : [{ key: "primary_userid", string: null }]),
  ];

  const primaryRepName = useMemo(() => {
    if (!userId) return null;
    const match = (userList ?? []).find((u) => String(u._id) === String(userId));
    return match?.name ?? `User #${userId}`;
  }, [userList, userId]);

  // fetches entity and sets up refetch
  const {
    data,
    loading: loadingEntity,
    refetch: refetchEntity,
  } = useQuery(entityQuery, {
    variables,
    skip: !isEdit,
    fetchPolicy: "network-only",
    onCompleted: (localData) => {
      const listResponse = localData?.crane;
      const list = listResponse?.edges ?? [];
      const crane = list[0];
      const tags = localData?.tag2Crane?.edges ?? [];
      const subscribedUsers = localData?.crane_subscription?.edges ?? [];
      setSubscribedUserList(subscribedUsers);
      setName(crane.name);
      setSerialNo(crane.serial_no);
      setLon(crane.longitude);
      setLat(crane.latitude);
      setRegionId(crane.region_id);
      setCustomerId(crane.customer_id);
      setLocation({ lon: crane.longitude, lat: crane.latitude });
      setUserId(crane.primary_userid);
      setRegionName(crane.region?.name ?? null);
      setCustomerName(crane.customer?.name ?? null);
      setTagIds(tags.map((tag) => tag.tag_id));
      setTagList(tags);
      setUserIds(subscribedUsers.map((item) => item.user_id))
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const validateForm = () => {
    const reasons = [];
    if (!name || name.trim() === "") reasons.push("Name is required.");
    if (!serialNo || serialNo.trim() === "") reasons.push("Serial Number is required.");
    // Longitude and latitude are optional, but if provided must be valid numbers
    if (lon !== null && lon !== "" && isNaN(parseFloat(lon))) reasons.push("Longitude must be a valid number.");
    if (lat !== null && lat !== "" && isNaN(parseFloat(lat))) reasons.push("Latitude must be a valid number.");
    // Primary Rep, Tags, and Subscriptions are optional
    setInvalidReasons(reasons);
    return reasons.length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    if (isEdit) mutateFunction();
    else createKeys.trackSave(() => mutateFunction());
  };

  const handleCancel = () => {
    setInvalidReasons([]);
    // Discard unsaved edits by reloading the persisted values.
    refetchEntity();
    setIsEditing(false);
  };

  // sets up Crane mutation
  const [mutateFunction, mutateInfo] = useMutation(entityMutation, {
    variables: {
      crane_mutation_request: mutationInput({
        id: isEdit ? entityId : undefined,
        properties: craneProperties,
        idempotencyKey: isEdit
          ? undefined
          : createKeys.keyFor("crane", fingerprintProperties(craneProperties)),
      }),
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
    onCompleted: (response, originalRequestVariables) => {
      let id = response?.crane?.id;
      setLocation({ lon: lon == '' ? null : lon, lat: lat == '' ? null : lat });
      mutateProject(id);
      navigate({
        action: "edit",
        id,
        push: false,
      });
      refetchEntity();
      setIsEditing(false);
    },
  });

  const mutateProject = async (crane_id) => {
    const tag2CraneMutateList = [];
    const userList = [];
    tagList.forEach((tag) => {
      if (!tagIds.includes(tag.tag_id)) {
        tag2CraneMutateList.push({ id: tag.id, tag_id: tag.tag_id, crane_id: crane_id });
      }
    });
    tagIds.forEach((id) => {
      if (!tagList.some((tag) => tag.tag_id == id)) {
        tag2CraneMutateList.push({ id: 0, tag_id: id, crane_id: crane_id });
      }
    });
    subscribedUserList.forEach((item) => {
      if (!userIds.includes(item.user_id)) {
        userList.push({ id: item.id, user_id: item.user_id });
      }
    });
    userIds.forEach((id) => {
      if (!subscribedUserList.some((item) => item.user_id == id)) {
        userList.push({ id: 0, user_id: id });
      }
    });
    const tag2CraneMutationPromises = tag2CraneMutateList.map((tag) => {
      client.mutate({
        mutation: tag2CraneMutation,
        variables: {
          tag2Crane_mutation_request: mutationInput({
            id: tag.id > 0 ? tag.id : undefined,
            shouldDelete: tag.id > 0,
            properties: [
              { key: "tag_id", number: Number(tag.tag_id) },
              { key: "crane_id", number: Number(tag.crane_id) },
            ],
            idempotencyKey:
              tag.id > 0
                ? undefined
                : createKeys.keyFor(`tag:${tag.tag_id}`, `${crane_id}:${tag.tag_id}`),
          }),
        },
      });
    })

    const subscribedUserMutationPromises = userList.map((item) => {
      client.mutate({
        mutation: craneSubscriptionMutation,
        variables: {
          crane_subscription_mutation_request: mutationInput({
            id: item.id > 0 ? item.id : undefined,
            shouldDelete: item.id > 0,
            properties: [
              { key: "user_id", string: item.user_id },
              { key: "crane_id", number: Number(crane_id) },
            ],
            idempotencyKey:
              item.id > 0
                ? undefined
                : createKeys.keyFor(`sub:${item.user_id}`, `${crane_id}:${item.user_id}`),
          }),
        },
      });
    })

    try {
      // Wait for all mutations to complete
      const results = await Promise.all([...tag2CraneMutationPromises, ...subscribedUserMutationPromises]);
      addNotification("good", "Crane saved successfully");

    } catch (error) {
      // Handle errors
      console.error("One or more mutations failed:", error);
      addNotification("bad", "Something went wrong");
    }
  }

  const updatePosition = (longitude, latitude) => {
    setLon(longitude);
    setLat(latitude);
    setLocation({ lon: parseFloat(longitude), lat: parseFloat(latitude) });
  }
  const isCoordinateValid = () =>
    location.lat >= -90 && location.lat <= 90 && location.lon >= -180 && location.lon <= 180;

  let loadingMutation = mutateInfo.loading;

  // Read-only callers never edit; everyone else toggles via the Edit button.
  const showForm = readOnly ? false : isEditing;
  const hasCoords = lat !== null && lat !== "" && lon !== null && lon !== "";

  return loadingEntity ? (
    <LoadingView />
  ) : (
    <>
      <div className="page-header">
        <span className="page-header-title">
          { readOnly ? "Crane Detail" : (isEdit ? "Edit" : "Add") + " Crane Detail" }
        </span>
      </div>

      <div className="add-edit">
        <div className="entity-edit-form">
          <div className="entity-form-header">
            <span className="entity-form-title">
              { name || (isEdit ? `Crane #${entityId}` : "New Crane") }
            </span>
            { !readOnly && !isEditing && (
              <button className="edit-toggle" onClick={ () => setIsEditing(true) }>
                Edit
              </button>
            ) }
          </div>

          { showForm ? (
            <>
              <div className="edit-section edit-grid">
                <div className="edit-property">
                  <label>Name *</label>
                  <input value={ name } onChange={ (e) => setName(e.target.value) } />
                </div>
                <div className="edit-property">
                  <label>Serial Number *</label>
                  <input value={ serialNo } onChange={ (e) => setSerialNo(e.target.value) } />
                </div>
                <div className="edit-property">
                  <label>Longitude</label>
                  <input
                    type="number"
                    value={ lon }
                    onChange={ (e) => setLon(e.target.value) }
                  />
                </div>
                <div className="edit-property">
                  <label>Latitude</label>
                  <input
                    type="number"
                    value={ lat }
                    onChange={ (e) => setLat(e.target.value) }
                  />
                </div>
                <div className="edit-property">
                  <label>Region</label>
                  <RegionDropDown
                    selected={ regionId }
                    onSelect={ setRegionId }
                    searchEnabled
                  />
                </div>
                <div className="edit-property">
                  <label>Customer</label>
                  <CustomerDropDown
                    selected={ customerId }
                    onSelect={ setCustomerId }
                    searchEnabled
                  />
                </div>
                <div className="edit-property">
                  <label>Primary Rep</label>
                  <UserDropDown selected={ userId } onSelect={ setUserId } searchEnabled />
                </div>
                <div className="edit-property">
                  <label>Tag</label>
                  <TagDropDown
                    selected={ tagIds }
                    onSelect={ setTagIds }
                    searchEnabled
                    multiple={ true }
                    entityId={ entityId }
                    entityType={ 'crane' }
                  />
                </div>
                <div className="edit-property">
                  <label>Assigned Technicians</label>
                  <NotificationDropDown
                    selected={ userIds }
                    onSelect={ setUserIds }
                    searchEnabled
                    multiple={ true }
                    entityId={ entityId }
                    entityType={ 'crane' }
                  />
                </div>
              </div>
              { invalidReasons.length > 0 && (
                <InvalidReasons reasons={ invalidReasons } />
              ) }
              <div className="edit-actions">
                { isEdit && (
                  <button className="secondary" onClick={ handleCancel }>
                    Cancel
                  </button>
                ) }
                <SaveButton
                  saving={ !isEdit && createKeys.isSaving }
                  disabled={ isEdit && loadingMutation }
                  onClick={ () => handleSave() }
                />
              </div>
            </>
          ) : (
            <div className="view-grid">
              <div className="view-item">
                <label>Serial Number</label>
                <span className="view-value">{ serialNo || "—" }</span>
              </div>
              <div className="view-item">
                <label>Region</label>
                <span className={ regionName ? "view-value" : "view-value muted" }>
                  { regionName || "Not set" }
                </span>
              </div>
              <div className="view-item">
                <label>Customer</label>
                <span className={ customerName ? "view-value" : "view-value muted" }>
                  { customerName || "Not set" }
                </span>
              </div>
              <div className="view-item">
                <label>Primary Rep</label>
                <span className={ primaryRepName ? "view-value" : "view-value muted" }>
                  { primaryRepName || "Not set" }
                </span>
              </div>
              <div className="view-item">
                <label>Location</label>
                <span className={ hasCoords ? "view-value" : "view-value muted" }>
                  { hasCoords ? `${lat}, ${lon}` : "Not set" }
                </span>
              </div>
            </div>
          ) }
        </div>

        { isEdit && (
          <div className="crane-edit-tabs">
            <Tabs autoSelect state={ [activeTab, setActiveTab] }>
            <div label="Chat">
              <CraneEvent crane_id={ Number(entityId) } />
            </div>
            <div label="Tickets">
              <StateStack path="/ticket">
                { (props) => (
                  <TicketController
                    { ...props }
                    style={ {
                      height: "600px",
                    } }
                    options={ {
                      crane_id: Number(entityId),
                    } }
                  />
                ) }
              </StateStack>
            </div>
            <div label="Services">
              <StateStack path="/service">
                { (props) => (
                  <ServiceController
                    { ...props }
                    style={ {
                      height: "600px",
                    } }
                    options={ {
                      crane_id: Number(entityId),
                    } }
                  />
                ) }
              </StateStack>
            </div>
            <div label="Map">
              <SingleView
                lon={ isCoordinateValid() ? location.lon : null }
                lat={ isCoordinateValid() ? location.lat : null }
                type="crane"
                updatePosition={ readOnly ? () => { } : updatePosition }
                style={ { height: "600px", position: "relative" } }
              />
            </div>
            <div label="Inspection Intervals">
              <div style={ { padding: "20px", height: "600px", overflow: "auto" } }>
                <InspectionIntervals craneId={ Number(entityId) } readOnly={ readOnly } />
              </div>
            </div>
            </Tabs>
          </div>
        ) }
      </div>
    </>
  );
}
